import Swal, { SweetAlertOptions } from "sweetalert2";

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const messageToHtml = (message: string): string =>
  escapeHtml(message).replace(/\n/g, "<br />");

// Configuração padrão do tema
const defaultTheme: SweetAlertOptions = {
  confirmButtonText: "OK",
  cancelButtonText: "Cancelar",
  confirmButtonColor: "#6366f1", // indigo-500
  cancelButtonColor: "#6b7280", // gray-500
  buttonsStyling: true,
  allowOutsideClick: false,
  allowEscapeKey: true,
  customClass: {
    container: "swal2-z-index-high",
    popup: "swal2-z-index-high",
  },
  didOpen: () => {
    // Garantir que os alertas apareçam acima dos modais (z-index 9999)
    const swalContainer = document.querySelector(
      ".swal2-container",
    ) as HTMLElement;
    if (swalContainer) {
      swalContainer.style.zIndex = "10000";
    }
  },
};

// Função base
const showAlert = (message: string, options: SweetAlertOptions = {}) =>
  Swal.fire({
    ...defaultTheme,
    html: messageToHtml(message),
    ...options,
  } as SweetAlertOptions);

// Funções padronizadas por tipo com suporte a opções extras
export const showSuccess = (
  message: string,
  title: string = "Sucesso",
  options: SweetAlertOptions = {},
) =>
  showAlert(message, {
    icon: "success",
    title,
    confirmButtonColor: "#10b981", // green-500
    ...options,
  });

export const showError = (
  message: string,
  title: string = "Erro",
  options: SweetAlertOptions = {},
) =>
  showAlert(message, {
    icon: "error",
    title,
    confirmButtonColor: "#ef4444", // red-500
    ...options,
  });

export const showWarning = (
  message: string,
  title: string = "Atenção",
  options: SweetAlertOptions = {},
) =>
  showAlert(message, {
    icon: "warning",
    title,
    confirmButtonColor: "#f59e0b", // amber-500
    ...options,
  });

export const showInfo = (
  message: string,
  title: string = "Informação",
  options: SweetAlertOptions = {},
) =>
  showAlert(message, {
    icon: "info",
    title,
    ...options,
  });

// Função para confirmação (substitui window.confirm)
export const showConfirm = (
  message: string,
  title: string = "Confirmar ação",
  confirmText: string = "Confirmar",
  cancelText: string = "Cancelar",
): Promise<boolean> =>
  Swal.fire({
    ...defaultTheme,
    title,
    html: messageToHtml(message),
    icon: "question",
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    confirmButtonColor: "#6366f1",
    cancelButtonColor: "#6b7280",
    reverseButtons: true,
  }).then((result) => result.isConfirmed);

// Função para input (substitui window.prompt)
export const showPrompt = (
  message: string,
  title: string = "Digite",
  inputPlaceholder: string = "",
  inputValue: string = "",
  confirmText: string = "Confirmar",
  cancelText: string = "Cancelar",
): Promise<string | null> => {
  return Swal.fire({
    title,
    html: messageToHtml(message),
    input: "text",
    inputPlaceholder,
    inputValue,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    confirmButtonColor: "#6366f1",
    cancelButtonColor: "#6b7280",
    buttonsStyling: true,
    allowOutsideClick: false,
    allowEscapeKey: true,
    reverseButtons: true,
  } as SweetAlertOptions).then((result) =>
    result.isConfirmed ? result.value : null,
  );
};

// Funções de Toasts (substitui componente Toast)
export const showToast = (
  message: string,
  type: "success" | "error" | "warning" | "info" = "success",
  duration: number = 4000,
) => {
  const typeClass = `premium-toast-${type}`;
  const isError = type === "error";

  const Toast = Swal.mixin({
    toast: true,
    position: "bottom-end",
    showConfirmButton: false,
    showCloseButton: true,
    timer: duration,
    timerProgressBar: true,
    width: "auto",
    padding: "0.5rem",
    customClass: {
      popup: `premium-toast-popup ${typeClass}`,
      htmlContainer: "premium-toast-content",
      icon: "premium-toast-icon",
      closeButton: "premium-toast-close",
    },
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer);
      toast.addEventListener("mouseleave", Swal.resumeTimer);

      const copyBtn = toast.querySelector(".premium-toast-copy-btn");
      if (copyBtn) {
        copyBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(message).then(() => {
            const icon = copyBtn.querySelector("svg");
            if (icon) icon.style.display = "none";
            const feedback = document.createElement("span");
            feedback.innerText = "✅";
            copyBtn.appendChild(feedback);
            setTimeout(() => {
              feedback.remove();
              if (icon) icon.style.display = "block";
            }, 1500);
          });
        });
      }
    },
  });

  const copyBtnHtml = isError
    ? `<button class="premium-toast-copy-btn" title="Copiar mensagem">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
       </button>`
    : "";

  return Toast.fire({
    icon: type,
    html: `
      <div class="premium-toast-message-container">
        <div class="premium-toast-message">${messageToHtml(message)}</div>
        ${copyBtnHtml}
      </div>
    `,
  });
};

// Funções de conveniência para toast
export const showToastSuccess = (message: string, duration?: number) =>
  showToast(message, "success", duration);

export const showToastError = (message: string, duration?: number) =>
  showToast(message, "error", duration);

export const showToastWarning = (message: string, duration?: number) =>
  showToast(message, "warning", duration);

export const showToastInfo = (message: string, duration?: number) =>
  showToast(message, "info", duration);

// Função de Loading (SweetAlert2)
export const showLoading = (
  message: string = "Processando...",
  title: string = "Aguarde",
) => {
  return Swal.fire({
    title,
    html: messageToHtml(message),
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => {
      Swal.showLoading();
      // Garantir que apareça acima de tudo
      const swalContainer = document.querySelector(
        ".swal2-container",
      ) as HTMLElement;
      if (swalContainer) {
        swalContainer.style.zIndex = "100000";
      }
    },
  });
};

/**
 * Mostra um modal com barra de progresso real.
 * @returns Um objeto com funções para atualizar o progresso ou fechar.
 */
export const showProgress = (title: string, initialMessage: string) => {
  let progress = 0;

  Swal.fire({
    title,
    html: `
      <div class="swal-progress-container" style="margin-top: 1.5rem;">
        <div id="swal-progress-text" style="font-size: 0.875rem; font-weight: 700; color: #4b5563; margin-bottom: 0.5rem; text-align: left;">${initialMessage}</div>
        <div style="width: 100%; background-color: #e5e7eb; border-radius: 9999px; height: 0.75rem; overflow: hidden; border: 1px solid #e2e8f0;">
          <div id="swal-progress-bar" style="width: 0%; height: 100%; background: linear-gradient(90deg, #6366f1 0%, #a855f7 100%); transition: width 0.3s ease; border-radius: 9999px;"></div>
        </div>
        <div id="swal-progress-percent" style="font-size: 0.75rem; font-weight: 800; color: #6366f1; margin-top: 0.4rem; text-align: right; letter-spacing: 0.05em;">0%</div>
      </div>
    `,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => {
      const swalContainer = document.querySelector(
        ".swal2-container",
      ) as HTMLElement;
      if (swalContainer) {
        swalContainer.style.zIndex = "100000";
      }
    },
  });

  return {
    update: (newProgress: number, newMessage?: string) => {
      progress = Math.min(Math.max(newProgress, 0), 100);
      const progressBar = document.getElementById("swal-progress-bar");
      const progressText = document.getElementById("swal-progress-text");
      const progressPercent = document.getElementById("swal-progress-percent");

      if (progressBar) progressBar.style.width = `${progress}%`;
      if (progressPercent)
        progressPercent.innerText = `${Math.round(progress)}%`;
      if (newMessage && progressText) progressText.innerText = newMessage;
    },
    close: () => {
      Swal.close();
    },
  };
};

export const closeLoading = () => {
  Swal.close();
};

// Exportar função genérica também
export { showAlert };
